import { Component, Injector, OnInit } from '@angular/core';
import { NavParams } from '@ionic/angular';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { Extender } from 'src/shared/helpers/extender';
import { Account, Comment, Feed } from 'src/shared/models';
import { FeedService } from '../../services/feed/feed.service';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss']
})
export class CommentComponent extends Extender implements OnInit {
  /**
   * @property me
   * @type Contact
   */
  public me: Account;

  /**
   * @property comments
   * @type Comment[]
   * @default []
   */
  public comments: Comment[] = [];

  /**
   * @property editComment
   * @type Comment
   */
  public editComment: Comment;

  /**
   * @property commentsBackUp
   * @type Comment[]
   * @default []
   */
  public commentsBackUp: Comment[] = [];

  /**
   * @property feed
   * @type Feed
   */
  public feed: Feed;

  /**
   * @property commentCtrl
   * @type FormControl
   * @default new FormControl()
   */
  public commentMessage: string = null;

  /**
   * @property openSearch
   * @type boolean
   * @default false
   */
  public openSearch: boolean = false;

  /**
   * capture user search data
   * @property search
   * @type Subject<string>
   * @public
   * @default new Subject<string>()
   */
  public search: Subject<string> = new Subject<string>();
  /**
   * @method constructor
   * @param navParams
   * @param actionSheetCtrl
   * @param commonProvider
   * @param viewCtrl
   * @param realTime
   * @param errorProvider
   */
  constructor(
    protected injector: Injector,
    private navParams: NavParams,
    private feedService: FeedService
  ) {
    super(injector);
  }

  /**
   * get current users contact
   * get real time references and subscribe to data
   * store data in comments and commentsBackUp
   * @method ngOnInit
   */
  public async ngOnInit() {
    this.feed = this.navParams.get('data');
    this.me = await this.auth$.getUser();
    this.feedService.getComments(this.feed.id).subscribe((feed) => {
      this.comments = feed;
    });
    this._searchSubscription();
  }

  /**
   * search comments
   * @method onSearch
   * @param {string} val
   */
  public onSearch(val: string) {
    this.comments = [...this.commentsBackUp];

    if (val && val.trim() !== '') {
      this.comments = this.comments.filter((item) => {
        return (
          item.text.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
          item.user.fullname.toLowerCase().indexOf(val.toLowerCase()) > -1
        );
      });
    }
  }

  /**
   * delete comments
   * @method delete
   * @param {Comment} comment
   */
  public delete(comment) {
    this.feedService.removeComment(comment);
  }

  /**
   * save comment
   * @method save
   * @param {value, valid} { value: any, valid: boolean }
   */
  public save(value) {
    if (this.editComment) {
      this.editComment.text = value;
      delete this.editComment.user;
      this.feedService.updateComment(this.editComment).then();
      this.editComment = null;
    } else {
      const commentObj: any = {
        uid: this.me.uid,
        createdAt: Date.now(),
        fid: this.feed.id,
        text: value
      };
      this.feedService.sendComment(commentObj).then();
    }
    this.commentMessage = '';
  }

  /**
   * @description manage comment by opening actionsheet and displaying options edit or delete comment.
   * @method openOptions
   * @param comment
   */
  public async openOptions(comment: Comment): Promise<any> {
    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translate.instant('Other.options'),
      buttons: [
        {
          text: this.translate.instant('Other.edit-label'),
          handler: () => {
            this.editComment = comment;
            this.commentMessage = comment.text;
          }
        },
        {
          text: this.translate.instant('Other.delete-label'),
          handler: () => {
            this.feedService.removeComment(comment);
          }
        },
        {
          text: this.translate.instant('Other.cancel'),
          role: 'cancel',
          handler: () => {}
        }
      ]
    });
    await actionSheet.present();
  }

  private _searchSubscription(): void {
    this.search
      .pipe(
        map((value) => value),
        debounceTime(600),
        distinctUntilChanged()
      )
      .subscribe((searchPhrase) => {
        this.onSearch(searchPhrase);
      });
  }
}
